import React, { useEffect, useMemo, useRef } from 'react'
import Button from '../../../Forms/Button'
import { captureEvent } from '../../../../analytics'
import { analyticsEvents } from '../../../../analytics/events'
import Loader from '../../../Loader'
import { useState } from 'react'
import { useDetectClickOutside } from 'react-detect-click-outside'
import { isProductionBuild } from '../../../../helpers/general'
import { ReactComponent as CheckmarkIcon } from './icons/checkmark.svg'
import { AppConfigContext } from '../../../../context/AppConfig'
import { useContext } from 'react'
import { useParentWindowEvent } from '../../../../hooks/useParentWindowEvent'
import { useStore } from '../../../../store'
function WelcomeModal({ onStart, modelLoaded, metadataLoaded, shown }) {
  const [timer, setTimer] = useState()

  function onContinue() {
    onStart()
  }

  const clickOutsideRef = useDetectClickOutside({
    onTriggered: () => {
      if (metadataLoaded) {
        onStart()
      }
    },
  })

  const ref = useRef()

  useEffect(() => {
    if (ref && ref.current) {
      ref.current.addEventListener('animationend', () => {
        ref.current.classList.add('hidden')
      })
    }
  }, [ref])

  useEffect(() => {
    if (metadataLoaded && !timer && shown) {
      if (timer) {
        clearTimeout(timer)
      }
      setTimer(
        setTimeout(() => {
          onStart()
        }, 7000),
      )
    }
  }, [onStart, metadataLoaded, timer, modelLoaded, shown])

  useEffect(() => {
    if (!shown && timer) {
      clearTimeout(timer)
      setTimer(null)
    }
  }, [shown, setTimer, timer])

  useEffect(() => {
    if (shown) {
      if (ref && ref.current) {
        ref.current.classList.remove('hidden')
      }
    }
  }, [shown, ref])

  const { isSWApp, isEWApp, isPDApp, isAvalonApp, single_line, staging_welcome } = useContext(AppConfigContext)

  const points = useMemo(() => {
    if (isAvalonApp) {
      return [
        'Browse the presented floor plan',
        'View professionally staged packages',
        'Connect with the property',
      ]
    }
    if (isSWApp || staging_welcome) {
      if (single_line) {
        return [
          'Browse the preset floor plan',
          'View professionally staged furniture packages',
          'Get inspiration for different styles and setups',
        ]
      } else {
        return [
          'Browse preset floor plans',
          'View professionally staged furniture packages',
          'Get inspiration for different styles and setups',
        ]
      }
    }
    return [
      'Select your floor plan',
      'Visually explore furniture packages',
      (() => {
        if (isEWApp) {
          return (
            <span>
              Customize packages and <br className="lg:hidden"></br>checkout at The Everset website
            </span>
          )
        }

        if (isPDApp) {
          return 'Connect with the property'
        }
        return 'Connect with CORT'
      })(),
    ]
  }, [isEWApp, isSWApp, isPDApp, isAvalonApp, single_line, staging_welcome])

  function getTitle() {
    if (isAvalonApp) {
      return 'Virtually Explore The Apartment In 3D!'
    }
    if (isEWApp) {
      return 'Easily turn your apartment into a home with The Everset turnkey packages! '
    }
    if (isSWApp || staging_welcome) {
      if (single_line) {
        return 'Virtually Explore The Apartment in 3D!'
      } else {
        return 'Virtually Explore Apartments in 3D!'
      }
    }
    return "Easily turn your apartment into a home with CORT's Move-In Ready® packages!"
  }

  const [hidden, setHidden] = useState(false)

  useParentWindowEvent({
    onClose: () => {
      setHidden(true)
    },
  })

  return (
    !hidden && (
      <>
        <div
          ref={ref}
          className={`fixed top-[0] left-[0] h-full bg-[rgba(0,0,0,.7)] w-full z-[500] flex justify-center items-center ${
            shown ? '' : 'fade-out'
          }`}
        >
          <div
            className="bg-white rounded-[8px] shadow landscape:w-[554px] w-[calc(100%-48px)] sm:!w-[430px] overflow-hidden"
            ref={clickOutsideRef}
          >
            <div className="px-[18px] landscape:px-[24px] py-[24px] lg:p-[40px] lg:landscape:px-[40px] lg:pt-[24px] text-[18px] font-medium landscape:flex block lg:landscape:block">
              <div className="flex justify-center items-center shrink-0">
                <Loader className="!h-[100px]" />
              </div>
              <div className="landscape:ml-[24px] ml-0 lg:landscape:ml-0 portrait:mt-[14px] lg:mt-[14px]">
                <div
                  className={`${
                    isSWApp || isEWApp ? 'text-black' : 'text-[var(--brand-color)]'
                  } leading-[22px] lg:leading-[24px]mt-[12px] lg:landscape:mt-[12px] text-[18px] font-medium portrait:font-semibold lg:font-semibold`}
                >
                  {getTitle()}
                </div>
                {/* <div className="leading-[22px] lg:leading-[24px] text-[16px] mt-[12px]  text-[#3D4256] font-normal">
              Select your floor plan, visually explore<br className="hidden lg:inline"></br> packages, and connect
              with CORT for<br className="hidden lg:inline"></br> personalized assistance.
            </div> */}
                <div className="mt-[15px]">
                  {points.map((p, index) => (
                    <div className="flex items-start mt-[10px] first:mt-0" key={index}>
                      <div className="flex-shrink-0">
                        <CheckmarkIcon />
                      </div>
                      <span className="ml-[12px] font-normal text-[16px] leading-[20px]">{p}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-[20px] portrait:mt-[32px] lg:mt-[32px] flex justify-center">
                  <Button
                    disabled={!metadataLoaded}
                    onClick={() => {
                      captureEvent(analyticsEvents.START_EXPLORING_TAPPED)
                      onContinue()
                    }}
                    className={`landscape:w-full w-[185px] lg:landscape:w-[185px] bg-black  ${
                      isAvalonApp ? '!bg-[var(--brand-color)]' : isSWApp || isEWApp ? 'lg:!bg-[var(--brand-color)]' : '!bg-black'
                    }`}
                  >
                    <span className="text-[16px]">Start Exploring</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  )
}

export default WelcomeModal
